/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */
import React, { useEffect, useState } from "react";
import {
    Card,
    Grid,
    TextField,
    Snackbar,
    Alert,
    CircularProgress,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "auth/Auth";
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";

/**
 * Org settings for PSA-189 merchant service request timing.
 * 0 days before departure = send immediately on payment.
 */
export default function MerchantServiceRequestSettings() {
    const isV2 = useTheme()?.pearlUi === "v2";
    const { axiosInstance } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        mol_service_request_days_before_departure: 0,
        mol_service_request_accept_days: 7,
        mol_final_confirmation_days_before_departure: 14,
    });
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const { data } = await axiosInstance.get("/company/info");
                setForm({
                    mol_service_request_days_before_departure:
                        data.mol_service_request_days_before_departure ?? 0,
                    mol_service_request_accept_days: data.mol_service_request_accept_days ?? 7,
                    mol_final_confirmation_days_before_departure:
                        data.mol_final_confirmation_days_before_departure ?? 14,
                });
            } catch (err) {
                setSnackbar({
                    open: true,
                    message: err.response?.data?.detail || "Failed to load settings",
                    severity: "error",
                });
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [axiosInstance]);

    const handleChange = (field) => (event) => {
        const value = event.target.value === "" ? "" : Number(event.target.value);
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await axiosInstance.put("/company/info", {
                mol_service_request_days_before_departure: Number(
                    form.mol_service_request_days_before_departure
                ) || 0,
                mol_service_request_accept_days: Math.max(
                    1,
                    Number(form.mol_service_request_accept_days) || 7
                ),
                mol_final_confirmation_days_before_departure: Math.max(
                    0,
                    Number(form.mol_final_confirmation_days_before_departure) || 0
                ),
            });
            setSnackbar({ open: true, message: "Settings saved", severity: "success" });
        } catch (err) {
            setSnackbar({
                open: true,
                message: err.response?.data?.detail || "Failed to save settings",
                severity: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={isV2 ? 0 : 6} pb={3}>
                {isV2 && (
                    <PageHeader
                        title="Merchant service requests"
                        summary="When service request emails go to merchants, how long they have to accept, and when final confirmations go out. Cancellation emails are always sent straight away."
                    />
                )}
                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Card>
                            <MDBox p={3}>
                                {!isV2 && (
                                    <>
                                        <MDTypography variant="h5" mb={1}>
                                            Merchant Service Request Settings
                                        </MDTypography>
                                        <MDTypography variant="body2" color="text" mb={3}>
                                            Configure when service request emails are sent to merchants, how long
                                            they have to accept, and when final confirmation emails go out.
                                            Cancellation emails are always sent immediately.
                                        </MDTypography>
                                    </>
                                )}

                                {loading ? (
                                    <CircularProgress />
                                ) : (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                type="number"
                                                label="Send service request (days before departure)"
                                                helperText="Use 0 to send immediately when payment succeeds"
                                                value={form.mol_service_request_days_before_departure}
                                                onChange={handleChange(
                                                    "mol_service_request_days_before_departure"
                                                )}
                                                inputProps={{ min: 0 }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                type="number"
                                                label="Days merchants have to accept"
                                                helperText="Accept/Reject links expire after this many days"
                                                value={form.mol_service_request_accept_days}
                                                onChange={handleChange("mol_service_request_accept_days")}
                                                inputProps={{ min: 1 }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                type="number"
                                                label="Final confirmation (days before departure)"
                                                helperText="Sent only for Booked lines within this window"
                                                value={form.mol_final_confirmation_days_before_departure}
                                                onChange={handleChange(
                                                    "mol_final_confirmation_days_before_departure"
                                                )}
                                                inputProps={{ min: 0 }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <MDButton
                                                variant="gradient"
                                                color="info"
                                                onClick={handleSave}
                                                disabled={saving}
                                            >
                                                {saving ? "Saving…" : "Save Settings"}
                                            </MDButton>
                                        </Grid>
                                    </Grid>
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
}
